/**
 * feat-256 — the "Available from" control: a Lesson's optional release instant,
 * living in the cream meta strip between the Active pill and `Delete Lesson`.
 *
 * Design authority: `design/feat-256-notes.md` §3, which is NORMATIVE. Copy is
 * quoted by number from §7. Every colour resolves in `tailwind.config.js` today —
 * this feature mints no token (notes §9).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE ONE RULE THAT MATTERS MOST HERE (notes §3.4, spec AC28/AC28b):
 *
 *   The control branches on `available_at` to pick its SHAPE, and on `active`
 *   only to pick between the `Available…` and `Held ·` PREFIXES. It NEVER
 *   branches on `active` to decide whether to be INTERACTIVE.
 *
 * If you find yourself adding `disabled={!unit.active}`, that is spec r1's
 * design, which r2 REVERSED. The reversal is not cosmetic: with the control
 * disabled while Hidden, the only route to a schedule is flip Active — at which
 * point the Lesson is live to every learner in its Tracks — and only THEN set the
 * date. That exposure window is precisely what this feature exists to prevent,
 * and it sits in the flow the feature starts from ("I'm Good", the Hidden Lesson
 * in the screenshot that opened the ticket). Editable-while-Hidden supports "set
 * the date, then flip Active" with no exposure at all.
 *
 * The mutation is subtle enough to need its own control (AC28b): under the wrong
 * design the chip STILL renders `Held · <instant>`, and that string is the half
 * AC28 also asserts — so a test checking only the string stays green while the
 * behaviour regresses. `LessonScheduleControl.feat256.test.tsx` asserts the DATE
 * button specifically, which is the element `disabled={!unit.active}` would kill.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * FOUR visual states, not five (notes §3.4). Hidden + no date renders
 * byte-identically to Active + no date — with no value to hold and no restriction
 * to explain, `active` changes nothing — so r1's fifth state and its copy string
 * 7.13 are both deleted.
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { QuizUnit } from '../../../services/quizContentAdminService';
import { QUIZ_TERMS } from '../quizTerminology';
import {
  formatPreciseInstant,
  formatReleaseInstant,
  formatReleasedDate,
  isReleased,
  localInputToUtcIso,
  POPOVER_MAX_WIDTH,
  resolvePopoverPlacement,
  resolvedTimeZone,
  shortTimeZoneName,
  utcIsoToLocalInput,
} from './lessonSchedule';

/** The chip shell — shared by all four variants so they cannot drift in shape. */
const CHIP_BASE = 'inline-flex items-center gap-1.5 rounded-full pl-2.5 pr-1 py-1 whitespace-nowrap';

/**
 * `whitespace-nowrap` above is REQUIRED, not tidiness: without it a chip breaks
 * mid-date on a narrow pane and `Available Wed Aug / 26, 9:00 AM PDT` is a
 * misreading waiting to happen (notes §3.3).
 */

type SaveStatus = 'idle' | 'saving' | 'failed';

export const LessonScheduleControl: React.FC<{
  unit: QuizUnit;
  onSave: (patch: Partial<{ available_at: string | null }>) => Promise<void>;
  /**
   * The element the popover must stay inside — `LessonMetaPanel`'s `<section>`,
   * which carries the `overflow-hidden` that was clipping it.
   *
   * A REF rather than `closest('section')`, deliberately (design r5 §4).
   * `closest` couples this component to an ancestor tag it does not own: if
   * anyone ever wraps the panel in another `<section>`, the clamp silently
   * retargets to the wrong box and the bug comes back LOOKING FIXED. Optional,
   * so the control still renders standalone in tests; it then falls back to the
   * document element.
   */
  clipRef?: React.RefObject<HTMLElement | null>;
}> = ({ unit, onSave, clipRef }) => {
  const persisted = unit.available_at ?? null;

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [status, setStatus] = useState<SaveStatus>('idle');
  /** The value the failed save was carrying — reopening the editor holds it. */
  const [attempted, setAttempted] = useState<string | null>(null);
  const [errorText, setErrorText] = useState('');

  const rootRef = useRef<HTMLDivElement>(null);

  /**
   * feat-256 followup — collision-aware placement, replacing the static
   * `left-0` that made the popover unreachable.
   *
   * `null` until measured; the popover renders unpositioned for that first
   * frame only. `useLayoutEffect` (not `useEffect`) is what keeps that frame
   * from being PAINTED — the panel lands in the right place instead of visibly
   * jumping.
   */
  const [placement, setPlacement] = useState<{ left: number; width: number } | null>(null);

  // A different Lesson selected in the tree reuses this component instance, so
  // an open popover (and a stale failure) must not follow the selection across.
  useEffect(() => {
    setOpen(false);
    setStatus('idle');
    setAttempted(null);
  }, [unit.id]);

  const closeEditor = useCallback(() => setOpen(false), []);

  /**
   * Measure on open, and again on resize while open.
   *
   * The anchor is `rootRef` — the wrapper holding the chip/button. The popover
   * is `position: absolute` and therefore out of flow, so it does not
   * contribute to that wrapper's box: the rect really is the control's.
   *
   * WHY THIS IS NEEDED AT ALL, worth keeping because it is not a popover bug:
   * the anchor's position is unstable BY CONSTRUCTION. The control is a flex
   * child of a `flex-wrap` strip whose wrap point moves with pane width, title
   * length, whether the Lesson has an image — and, the thing that actually bit
   * us, whether the control is the 89px `＋ Schedule` button or the 250px chip.
   * The narrow button packs onto the previous line and sits ~293px further
   * right, which is why the bug lives in the EMPTY state and why a fix checked
   * only against an already-scheduled Lesson looks correct and ships broken.
   * Anything anchored to a child of this strip must be collision-clamped, never
   * statically sided.
   */
  useLayoutEffect(() => {
    if (!open) {
      setPlacement(null);
      return undefined;
    }
    const measure = () => {
      const anchorEl = rootRef.current;
      if (!anchorEl) return;
      const clipEl: Element = clipRef?.current ?? document.documentElement;
      const { width, offset } = resolvePopoverPlacement({
        anchor: anchorEl.getBoundingClientRect(),
        clip: clipEl.getBoundingClientRect(),
      });
      // `offset` is relative to the anchor, which is what `left` means for an
      // absolutely-positioned child of the anchor's `relative` wrapper.
      setPlacement({ left: offset, width });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [open, clipRef]);

  // Closes on Escape (DISCARDS — notes §3.5) and on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeEditor();
    };
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) closeEditor();
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open, closeEditor]);

  /**
   * Open the editor holding the value the user should see: the ATTEMPTED value
   * after a failure (so a retry does not silently discard what they typed), the
   * persisted value otherwise.
   *
   * NOTE there is deliberately no `active` term anywhere in this function.
   */
  const openEditor = () => {
    setDraft(utcIsoToLocalInput(status === 'failed' ? attempted : persisted));
    setOpen(true);
  };

  /**
   * Commit. `next` is an ISO-8601 UTC string to SET, or explicit `null` to CLEAR
   * (spec AC23) — never `undefined`, which the backend's `exclude_unset` drops as
   * a silent no-op, and never `''`.
   *
   * On failure the chip goes to the failed state but the UI keeps rendering the
   * PERSISTED value underneath, never the attempted one: leaving the screen
   * asserting a value the server does not have is the one outcome worse than a
   * visible error (notes §3.6).
   */
  const commit = async (next: string | null) => {
    setOpen(false);
    setStatus('saving');
    setAttempted(next);
    try {
      await onSave({ available_at: next });
      setStatus('idle');
      setAttempted(null);
      setErrorText('');
    } catch (err) {
      setErrorText(err instanceof Error ? err.message : String(err));
      setStatus('failed');
    }
  };

  const instant = formatReleaseInstant(persisted);
  const precise = formatPreciseInstant(persisted);
  // `instant === null` covers BOTH "no date" and "unparseable", which is why the
  // shape test below is on the formatted value rather than on `persisted`:
  // an unparseable value falls back to the empty affordance and `Invalid Date`
  // never reaches the screen (notes §7.3 / feat-252 AC-F3).
  const hasDate = instant !== null;

  const timeZone = resolvedTimeZone();
  const tzShort = shortTimeZoneName();

  /* ── the popover (notes §3.5) ─────────────────────────────────────────────── */
  const editor = open && (
    <div
      data-testid="lesson-schedule-popover"
      /* `top-full mt-2` — ALWAYS below the anchor. There is deliberately no
         flip-to-top branch: the panel is 181px (211px with the Hidden note),
         the chip's bottom sits 172px into the pane, and the pane's floor is
         480px — 119px of slack, 79px in the worst case. Flip logic here would
         be unreachable code that rots. (design r5 §4)

         No `left-0` and no `w-[276px]`: both are now measured. That static
         `left-0` WAS the bug. */
      className="absolute top-full mt-2 z-30 bg-white border border-softBorder rounded-2xl shadow-lg p-3 space-y-2"
      style={{
        left: placement ? `${placement.left}px` : 0,
        width: placement ? `${placement.width}px` : POPOVER_MAX_WIDTH,
      }}
    >
      <label
        className="block text-xs font-bold text-inkMuted"
        htmlFor={`lesson-schedule-input-${unit.id}`}
      >
        Release date
      </label>
      <input
        id={`lesson-schedule-input-${unit.id}`}
        data-testid="lesson-schedule-input"
        type="datetime-local"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        className="w-full text-sm bg-white border border-softBorder rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-coral/40"
      />

      {/* 7.12 — ON SCREEN, only while Hidden (design r3).
          r2 had this sentence in the `Held ·` chip's `title`, and that was wrong
          in the state that can least afford it: Hidden + dated is the most
          confusable state in the feature — a user can set Wednesday, watch
          Wednesday arrive, and see nothing. A `title` needs a hover, does not
          exist on touch, and is announced inconsistently by screen readers, so
          the one string explaining the most confusable state was the one string
          a large share of users could not reach.

          The rule that replaced it, binding on every chip here: a `title` may
          carry PRECISION the visible text abbreviates; it may never carry
          MEANING the visible text omits.

          Dashed border on cream, NOT `attention` amber — a dated Hidden Lesson
          is a legitimate mid-workflow state, not an author-fixable fault, and
          amber invites someone to "fix" a Lesson that is exactly as intended.
          The dashed treatment matches the `Held ·` chip this popover was opened
          from, tying the explanation to the thing being explained. */}
      {!unit.active && (
        <p
          data-testid="lesson-schedule-hidden-note"
          className="text-[11px] text-inkMuted leading-snug bg-cream border border-dashed border-inkMuted/25 rounded-xl px-2 py-1.5"
        >
          This {QUIZ_TERMS.lesson.singular} is Hidden, so nothing releases on this date — it's saved and
          waiting. Set it Active when you're ready.
        </p>
      )}

      {/* 7.9 / 7.10. The zone is stated twice on purpose (notes §2.4): short in
          the chip, long with the IANA name here. The second sentence exists
          because per-location release was considered and deferred — a manager in
          California scheduling a Hawaii location needs to know the instant is
          global at the moment they pick it. */}
      <p data-testid="lesson-schedule-tz-note" className="text-[11px] text-inkMuted leading-snug">
        Times are in your timezone — {tzShort} ({timeZone}). Every learner sees this{' '}
        {QUIZ_TERMS.lesson.singular} at the same instant, wherever they are.
      </p>

      <div className="flex items-center justify-between pt-0.5">
        {/* Only when there is something to clear. On an unscheduled Lesson
            `Clear` would send `available_at: null` over a column that is already
            null — a destructive-looking control whose only effect is a wasted
            write. Abandoning a half-typed date is what Escape is for. */}
        {hasDate ? (
          <button
            type="button"
            data-testid="lesson-schedule-popover-clear"
            onClick={() => commit(null)}
            className="text-xs font-semibold text-coral hover:underline"
          >
            Clear
          </button>
        ) : (
          <span />
        )}
        <button
          type="button"
          data-testid="lesson-schedule-done"
          onClick={() => commit(localInputToUtcIso(draft))}
          className="text-xs font-semibold rounded-full px-3 py-1.5 bg-coral text-white"
        >
          Done
        </button>
      </div>
    </div>
  );

  /* ── saving (notes §3.6) ──────────────────────────────────────────────────── */
  // Same fill as the scheduled chip, so committing does not flash a second
  // colour and back. `animate-pulse-slow` and NOT `animate-pulse`: this app's
  // tailwind config puts `keyframes`/`animation` BESIDE `extend`, so they
  // REPLACE Tailwind's defaults and only spin / pulse-slow / slide-in-* survive.
  // `animate-pulse` compiles to nothing and renders an inert saving state that
  // tests green.
  if (status === 'saving') {
    return (
      <span
        data-testid="lesson-schedule-chip"
        aria-busy="true"
        className={`${CHIP_BASE} bg-preview-light text-preview cursor-progress`}
      >
        <span
          className="w-2 h-2 rounded-full border-[1.5px] border-preview animate-pulse-slow"
          aria-hidden="true"
        />
        <span className="text-xs font-bold pr-1">Saving…</span>
      </span>
    );
  }

  /* ── failed (notes §3.6) ──────────────────────────────────────────────────── */
  // Clicking reopens the editor holding the ATTEMPTED value; the chip itself
  // still stands for the persisted one.
  if (status === 'failed') {
    return (
      <div className="relative" ref={rootRef}>
        <span
          data-testid="lesson-schedule-chip"
          title={errorText || undefined}
          className={`${CHIP_BASE} bg-danger-surface border border-danger-line text-danger-text`}
        >
          <button
            type="button"
            data-testid="lesson-schedule-date"
            onClick={openEditor}
            className="text-xs font-bold"
          >
            Couldn't save — retry
          </button>
          <span className="text-[13px] leading-none pr-1" aria-hidden="true">
            ↻
          </span>
        </span>
        {editor}
      </div>
    );
  }

  /* ── empty (notes §3.2) ───────────────────────────────────────────────────── */
  // Rendered IDENTICALLY whether the Lesson is Active or Hidden. With no date
  // there is nothing to hold and nothing to explain, so `active` makes no
  // difference to this state — this is the collapse of r1's fifth state.
  //
  // `＋` is U+FF0B FULLWIDTH PLUS, matching the `IconPickerPopover` placeholder
  // already in this same strip. Do NOT substitute ASCII `+`; the two render at
  // visibly different weights side by side.
  //
  // Dashed, unfilled, muted — the strip's own idiom for an empty slot. AC22 says
  // a Lesson nobody schedules must look essentially unchanged, and this is the
  // quietest affordance that is still findable. Measured at 0px of added strip
  // height down to a 1280px window, which is where all 41 existing Lessons sit.
  if (!hasDate) {
    return (
      <div className="relative" ref={rootRef}>
        <button
          type="button"
          data-testid="lesson-schedule-add"
          onClick={openEditor}
          className="text-xs font-bold rounded-full px-2.5 py-1 border border-dashed border-inkMuted/25 text-inkMuted hover:bg-white hover:text-ink focus:outline-none focus:ring-2 focus:ring-coral/40"
        >
          ＋ Schedule
        </button>
        {editor}
      </div>
    );
  }

  /* ── set: one of three chips ──────────────────────────────────────────────── */
  const released = isReleased(persisted);

  // The Hidden variant is chosen FIRST and on `active` alone, because while
  // Hidden the past/future distinction is meaningless — nothing releases either
  // way (notes §2.3). This is the ONLY place `active` appears in the render, and
  // it picks a PREFIX, never interactivity.
  const heldWhileHidden = !unit.active;

  let chipClass: string;
  let text: string;
  let title: string | undefined;
  let ring: React.ReactNode = null;

  if (heldWhileHidden) {
    // notes §3.4 — 7.4 / 7.17. What carries "inert" now that it is no longer
    // carried by being disabled: the dashed border (this strip's idiom for
    // "present but not acting"), the absence of both the `preview` fill that
    // means SCHEDULED and the hollow ring that means PENDING, and the word
    // `Held`, which states the mechanism instead of implying it.
    //
    // No `opacity-*` of any kind, deliberately: the user must be able to READ
    // the date they set, and a dimmed date is a date you squint at.
    //
    // The `title` is the precise instant and NOTHING ELSE (7.17). The
    // explanation lives on screen in the popover — see the note on 7.12 above.
    chipClass = `${CHIP_BASE} bg-white border border-dashed border-inkMuted/25 text-inkMuted hover:bg-cream`;
    text = `Held · ${instant}`;
    title = precise ?? undefined;
  } else if (released) {
    // notes §3.3 past — 7.3 / 7.18. Plain: no fill, and no ring.
    chipClass = `${CHIP_BASE} bg-white border border-softBorder text-inkMuted`;
    text = `Available since ${formatReleasedDate(persisted)}`;
    title = precise ? `Released ${precise}` : undefined;
  } else {
    // notes §3.3 future — 7.2. `preview` slate, plus the HOLLOW ring.
    //
    // The hollow ring against the Active pill's FILLED dot is the load-bearing
    // pairing: filled = live, hollow = pending. It carries the meaning without a
    // glyph font and survives greyscale and every CVD type — colour is never the
    // only signal. Those two, plus the word `since`, are the three independent
    // signals AC26 asserts on; "some date is present" distinguishes nothing,
    // because both variants contain a date.
    //
    // `preview` and not feat-241's gold: gold means PROGRESS ON A METRIC and
    // amber means A PROBLEM YOU CAN FIX. A scheduled Lesson is neither — it is
    // legitimate, and not the real thing yet, which is what `preview` already
    // means in this system. Amber would invite someone to un-schedule it.
    chipClass = `${CHIP_BASE} bg-preview-light text-preview`;
    text = `Available ${instant}`;
    title = precise ?? undefined;
    ring = <span className="w-2 h-2 rounded-full border-[1.5px] border-preview" aria-hidden="true" />;
  }

  return (
    <div className="relative" ref={rootRef}>
      {/* Two SIBLING buttons inside a `<span>` wrapper — never a nested button
          (notes §3.3). The date opens the editor; the `×` clears. */}
      <span data-testid="lesson-schedule-chip" title={title} className={chipClass}>
        {ring}
        {/* AC28b's non-vacuity hinge. This button carries its own testid because
            querying for "a button" inside the chip matches the CLEAR button and
            passes VACUOUSLY while the date button is dead — and the date button
            is the one `disabled={!unit.active}` would kill. Note there is no
            `disabled` here at all, in any variant. */}
        <button
          type="button"
          data-testid="lesson-schedule-date"
          onClick={openEditor}
          className="text-xs font-bold"
        >
          {text}
        </button>
        <button
          type="button"
          data-testid="lesson-schedule-clear"
          aria-label="Clear release date"
          onClick={() => commit(null)}
          // The hover surface has to CONTRAST with the chip it sits on. `bg-white`
          // on the two white chips (released, `Held ·`) is invisible feedback —
          // the mockup used `bg-cream` there, and only the `preview`-filled
          // future chip is dark enough for white to read as a highlight.
          className={`w-4 h-4 rounded-full flex items-center justify-center text-[13px] leading-none ${
            heldWhileHidden || released ? 'text-inkMuted hover:bg-cream' : 'text-preview hover:bg-white'
          }`}
        >
          ×
        </button>
      </span>
      {editor}
    </div>
  );
};

export default LessonScheduleControl;
